import { PageLocationSP } from 'views/components/atoms/pageLocation';
import './styles.scss';
import { Tabs } from 'views/components/atoms/tabs';
import { SecuritySettingType } from 'views/constants/enums/securitySetting';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from 'views/components/atoms/toast';
import { ChangePasswordBody } from '../changePassword';
import { Loading } from 'views/components/atoms/loading';
import { formatDateTimeToSlashDateTime } from 'views/utils/format/FormatDate';
import { PasskeyAuth, PasskeyPageStep, useSecuritySetting, usePasskeyManage } from './hooks';
import { Button, ButtonSize, FillType } from 'views/components/atoms/button';
import { IconName, findIcon } from 'views/utils/handleIcon';
import { IconColor, IconSize, SvgIcon } from 'views/components/atoms/svgIcon';
import { SendSMSTemplate } from 'views/components/templates/sendSMS';

const getSecuritySettingTypeFromTab = (tab: string | null) =>
  tab === '1' ? SecuritySettingType.PasskeyManager : SecuritySettingType.PasswordChange;

export const SecuritySetting = () => {
  // パスキーの登録上限
  const passkeyMaxNum = 3;

  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [selectedSecuritySettingType, setSelectedSecuritySettingType] = useState<number>(
    getSecuritySettingTypeFromTab(tab),
  );

  const { getListAuthsState, passkeyList } = useSecuritySetting();
  const trashIcon = findIcon(IconName.Trash);

  const { passkeyPageStep, initPasskeyPageStep, onClickSignUp, onClickSignOut, authPageProps, toastProps } =
    usePasskeyManage();

  useEffect(() => {
    setSelectedSecuritySettingType(getSecuritySettingTypeFromTab(tab));
  }, [tab]);

  const renderChangePassword = () => {
    return (
      <div className="changePassword_form">
        <ChangePasswordBody isModal={false} />
      </div>
    );
  };

  const renderPasskeyIcon = (auth: PasskeyAuth) => {
    if (auth.IconData) {
      return <img className="passkeyManager_iconImage" src={auth.IconData} alt="パスキー" />;
    }

    return <span className="passkeyManager_iconFallback">{auth.AuthName?.charAt(0) || 'P'}</span>;
  };

  const renderPasskeyManager = () => {
    const isError = getListAuthsState.error || getListAuthsState.data?.GetListAuths?.Code !== 0;
    const showEmptyMessage = isError || passkeyList.length === 0;

    return (
      <div className="passkeyManagerWapper">
        {/* パスキーの新規登録 */}
        {passkeyPageStep === PasskeyPageStep.SignUpAuth && (
          <SendSMSTemplate {...authPageProps} toastProps={toastProps} />
        )}
        {/* 選択したパスキーの削除 */}
        {passkeyPageStep === PasskeyPageStep.SignOutAuth && (
          <SendSMSTemplate {...authPageProps} toastProps={toastProps} />
        )}
        {/* 登録済みのパスキーの一覧表示 */}
        {passkeyPageStep === PasskeyPageStep.Normal && (
          <>
            {getListAuthsState.fetching ? (
              <div className="passkeyManager_status">
                <Loading />
              </div>
            ) : showEmptyMessage ? (
              <div className="passkeyManager_message">登録済みのパスキーはありません</div>
            ) : (
              <ul className="passkeyManager_list">
                {passkeyList.map((auth) => (
                  <li className="passkeyManager_item" key={auth.CredentialID}>
                    <div className="passkeyManager_icon">{renderPasskeyIcon(auth)}</div>
                    <div className="passkeyManager_body">
                      <div className="passkeyManager_name">{auth.AuthName || 'パスキー'}</div>
                      <div className="passkeyManager_createdAt">
                        登録日：{formatDateTimeToSlashDateTime(auth.CreatedAt)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="passkeyManager_deleteButton"
                      aria-label="パスキー削除"
                      onClick={() => onClickSignOut(auth.CredentialID)}
                    >
                      <SvgIcon icon={trashIcon} size={IconSize.icon_m} color={IconColor.danger} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!getListAuthsState.fetching && (
              <div className="passkeyManager_buttonWrapper">
                <Button
                  label="パスキー登録"
                  fillType={FillType.primaryFill}
                  buttonSize={ButtonSize.sizeM}
                  disabled={passkeyList.length >= passkeyMaxNum}
                  onClick={onClickSignUp}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <section className="outletWrapper">
      <div className="centerContent">
        <PageLocationSP />

        <Tabs
          tabLists={[{ title: 'パスワード変更' }, { title: 'パスキー管理' }]}
          tabContents={[renderChangePassword(), renderPasskeyManager()]}
          selectedIndex={selectedSecuritySettingType - 1}
          onSelect={(index) => {
            setSelectedSecuritySettingType(index + 1);
            initPasskeyPageStep();
          }}
        />
        {passkeyPageStep === PasskeyPageStep.Normal && <Toast {...toastProps} />}
      </div>
    </section>
  );
};
